import React, { useEffect, useRef, useState } from 'react'
import {
  Avatar as MuiAvatar,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  makeStyles,
  Typography,
  CircularProgress
} from '@material-ui/core'
import { FileUploadOutlined, DeleteOutlined, InfoRounded } from '@material-ui/icons'
import { storageRef } from '../index'
import { Box, Fade } from '@mui/material'
import Tooltip from '@material-ui/core/Tooltip'
import { toast } from 'utils/notify'
import ImageCropModal from './ImageCropModal'

const UploadStatus = {
  uploading: 'Uploading',
  removing: 'Removing'
}

const AvatarPicker = (props) => {
  const {
    title,
    subTitle,
    path,
    imageName,
    resetImage = false,
    conferenceId,
    handleCustomChange,
    infoText,
    onDelete,
    onUpload,
    firsTimeUpload,
    imageProps = {},
    imageStyles = {},
    cropOnSelect = false
  } = props
  const classes = useStyles()
  const fileInputRef = useRef()
  const [image, setImage] = useState(null)
  const [currentImage, setCurrentImage] = useState(null)
  const [loading, setLoading] = useState(false)
  const [enableAction, setEnableAction] = useState(false)
  const [uploadStatus, setUploadStatus] = useState(null)
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [originalFileName, setOriginalFileName] = useState('')

  const downloadImage = async () => {
    try {
      setLoading(true)
      const imageUrl = await storageRef
        .child(`${conferenceId}/${path}/${imageName}`)
        .getDownloadURL()
      setImage(imageUrl)
      setCurrentImage(imageName)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (imageName) {
      downloadImage()
    }
  }, [imageName])

  useEffect(() => {
    if (resetImage) {
      setCurrentImage(null)
      setImage(null)
    }
    return () => {
      setCurrentImage(null)
      setImage(null)
    }
  }, [resetImage])

  const handleChange = async (event) => {
    const file = event.target.files[0]
    if (!file) return
    const allowedFileTypes = ['image/jpg', 'image/jpeg', 'image/png']
    if (!allowedFileTypes.includes(file.type)) {
      toast.warning('Only JPG and PNG images are supported.')
      return
    }

    // Store original file info for cropping
    setOriginalFileName(file.name)

    if (cropOnSelect) {
      // Open crop modal instead of immediate processing
      setImage(URL.createObjectURL(file))
      setCropModalOpen(true)
    } else {
      // Original behavior - no cropping
      setImage(URL.createObjectURL(file))
      if (firsTimeUpload) {
        handleCustomChange(file)
      } else {
        setUploadStatus(UploadStatus.uploading)
        setEnableAction(true)
        const timestamp = new Date().getTime() + ''
        await storageRef.child(`${conferenceId}/${path}/${timestamp}`).put(file)
        await onUpload(timestamp)
        setCurrentImage(timestamp)
        setEnableAction(false)
        setUploadStatus(null)
      }
    }
    event.target.value = null
  }

  const handleDeleteFile = async () => {
    setEnableAction(true)
    setUploadStatus(UploadStatus.removing)
    await storageRef.child(`${conferenceId}/${path}/${currentImage}`).delete()
    await onDelete()
    setEnableAction(false)
    setUploadStatus(null)
  }

  const buttonClickHandler = async () => {
    if (currentImage || image) {
      !firsTimeUpload && (await handleDeleteFile())
      setImage(null)
      setCurrentImage(null)
    } else {
      fileInputRef.current.click()
    }
  }

  const handleCropComplete = async (croppedFile) => {
    try {
      // Close modal first
      setCropModalOpen(false)
      setOriginalFileName('')

      // Update image preview with cropped file
      setImage(URL.createObjectURL(croppedFile))

      if (firsTimeUpload) {
        // Pass the cropped file to parent component
        handleCustomChange(croppedFile)
      } else {
        // Upload the cropped file directly
        setUploadStatus(UploadStatus.uploading)
        setEnableAction(true)
        const timestamp = new Date().getTime() + ''
        await storageRef.child(`${conferenceId}/${path}/${timestamp}`).put(croppedFile)
        await onUpload(timestamp)
        setCurrentImage(timestamp)
        setEnableAction(false)
        setUploadStatus(null)
      }
    } catch (error) {
      console.error('Error handling cropped image:', error)
      toast.error('Image processing failed. Please try again.')
    }
  }

  const handleCropCancel = () => {
    setCropModalOpen(false)
    setImage(null)
    setOriginalFileName('')
  }

  return (
    <>
      <Box className={classes.heading}>
        <Typography variant="h6" color="secondary">
          {title}
        </Typography>
        {infoText && (
          <Tooltip
            arrow
            TransitionComponent={Fade}
            title={<Typography className={classes.tooltip}>{infoText}</Typography>}>
            <IconButton className={classes.infoIconBtn}>
              <InfoRounded className={classes.infoIcon} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="body2" color="textSecondary" className={classes.subTitle}>
        {subTitle}
      </Typography>

      <Card variant="elevation">
        <CardContent className={classes.cardContent}>
          {loading ? (
            <Box className={classes.loadingBox}>
              <CircularProgress />
            </Box>
          ) : image ? (
            <MuiAvatar
              variant="square"
              imgProps={imageProps}
              className={classes.image}
              style={imageStyles}
              src={image}
            />
          ) : (
            <Box className={classes.emptyImageBox}>
              <FileUploadOutlined className={classes.uploadIcon} />
              <Typography variant="body2" color="textSecondary">
                Upload Image
              </Typography>
            </Box>
          )}
        </CardContent>
        <Divider />
        <Box>
          <input hidden accept="image/*" type="file" onChange={handleChange} ref={fileInputRef} />
          <Button
            fullWidth
            disabled={enableAction || loading}
            color="primary"
            variant="text"
            className={classes.button}
            startIcon={
              enableAction ? (
                <CircularProgress size={15} style={{ marginRight: 10 }} />
              ) : currentImage || image ? (
                <DeleteOutlined />
              ) : (
                <FileUploadOutlined />
              )
            }
            onClick={buttonClickHandler}>
            {uploadStatus ? uploadStatus : image || currentImage ? 'Remove' : 'Upload Image'}
          </Button>
        </Box>
      </Card>

      {/* Image Crop Modal */}
      <ImageCropModal
        open={cropModalOpen}
        onClose={handleCropCancel}
        imageSrc={image}
        fileName={originalFileName}
        onCropComplete={handleCropComplete}
        title={`Crop ${title}`}
      />
    </>
  )
}

export default AvatarPicker

const useStyles = makeStyles({
  heading: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    marginBottom: '8px',
    fontSize: '15px'
  },
  tooltip: {
    padding: '3px',
    fontSize: '13px'
  },
  infoIcon: {
    color: '#15b4db'
  },
  infoIconBtn: {
    padding: '2px'
  },
  button: {
    padding: '10px'
  },
  emptyImageBox: {
    height: '180px',
    width: '180px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #e0e0e0',
    borderRadius: '8px',
    margin: '16px',
    backgroundColor: '#f9f9f9'
  },
  uploadIcon: {
    fontSize: '40px',
    color: '#bdbdbd',
    marginBottom: '8px'
  },
  image: {
    minHeight: '140px',
    height: '140px',
    width: '150px',
    margin: '16px'
  },
  loadingBox: {
    height: '130px',
    width: '150px',
    margin: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'center'
  },
  subTitle: { marginBottom: 10, fontSize: '13px', fontStyle: 'italic' }
})
